import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './api-context';
import { connectSse } from './sse';
import { deriveJobUi, TERMINAL_JOB_STATUSES, type JobUiState } from '../state/job';
import type { Job } from './types';

export interface UseJobResult {
  job: Job | null;
  ui: JobUiState;
  isPending: boolean;
  error: Error | null;
}

/**
 * Track a durable job (§4.4, §6.4): poll `GET /jobs/{id}` every second, and
 * open the §4.4.7 SSE stream only to force a quick reconciliation when a
 * terminal event arrives. The polled `Job` remains the authoritative state.
 */
export function useJob(jobId: string | null, initialJob: Job | null): UseJobResult {
  const api = useApi();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.getJob(jobId as string),
    enabled: jobId !== null,
    initialData: initialJob ?? undefined,
    refetchInterval: (q) => {
      const job = q.state.data as Job | undefined;
      if (!job) return 1000;
      return (TERMINAL_JOB_STATUSES as readonly string[]).includes(job.status) ? false : 1000;
    },
  });

  const job = query.data ?? null;
  const terminal =
    job !== null && (TERMINAL_JOB_STATUSES as readonly string[]).includes(job.status);

  useEffect(() => {
    // A finished job has nothing left to stream; close the connection with polling.
    if (jobId === null || terminal) return undefined;
    const connection = connectSse({
      url: api.jobEventsUrl(jobId),
      headers: api.jobEventsHeaders(),
      onEvent: (event) => {
        if (event.event === 'complete' || event.event === 'error' || event.event === 'failed') {
          void queryClient.invalidateQueries({ queryKey: ['job', jobId] });
        }
      },
    });
    return () => connection.abort();
  }, [api, jobId, queryClient, terminal]);

  return { job, ui: deriveJobUi(job), isPending: query.isPending, error: query.error };
}
